import React, { useEffect, useRef } from "react";

/**
 * TradingView Advanced Chart widget with full drawing tools + pro toolbars.
 *
 * Accepts either `symbol` (our internal ticker like "BTCUSD", "EURUSD",
 * "XAUUSD", "MCX_GOLD") or `tvSymbol` (a fully-qualified TV symbol like
 * "BINANCE:BTCUSDT" or "OANDA:XAUUSD"). When `symbol` is provided it's
 * mapped to the appropriate TV exchange-qualified symbol via `_toTvSymbol`.
 */

// Internal Tradelog ticker → TradingView-recognised exchange-qualified
// symbol. Kept small on purpose — anything not in the map falls back to
// `FX:{symbol}` which correctly resolves for every forex pair TradingView
// carries. Crypto goes to Binance perps (matches our live-price feed),
// metals + oil to OANDA (broker-quality spot), indices to their CFD
// venues, MCX contracts to the MCX exchange prefix.
const TV_SYMBOL_MAP = {
  BTCUSD: "BINANCE:BTCUSDT",
  ETHUSD: "BINANCE:ETHUSDT",
  XAUUSD: "OANDA:XAUUSD",
  XAGUSD: "OANDA:XAGUSD",
  USOIL:  "TVC:USOIL",
  US30:   "CAPITALCOM:US30",
  NAS100: "CAPITALCOM:US100",
  SPX500: "OANDA:SPX500USD",
  GER40:  "PEPPERSTONE:GER40",
  MCX_GOLD:     "MCX:GOLD1!",
  MCX_SILVER:   "MCX:SILVER1!",
  MCX_CRUDEOIL: "MCX:CRUDEOIL1!",
};

function _toTvSymbol(raw) {
  if (!raw) return "FX:EURUSD";
  const s = String(raw).trim().toUpperCase();
  // Already exchange-qualified (contains a colon) — pass through.
  if (s.includes(":")) return s;
  if (TV_SYMBOL_MAP[s]) return TV_SYMBOL_MAP[s];
  // 6-letter FX ticker (EURUSD, GBPJPY…) — default to FX: prefix.
  if (/^[A-Z]{6}$/.test(s)) return `FX:${s}`;
  return s;
}

export default function TradingViewChart({ symbol, tvSymbol, interval = "60", height = 520 }) {
  const resolvedSymbol = tvSymbol || _toTvSymbol(symbol);
  const containerRef = useRef(null);
  const uid = useRef(`tv_${Math.random().toString(36).slice(2, 9)}`);

  useEffect(() => {
    if (!containerRef.current) return;
    containerRef.current.innerHTML = `<div id="${uid.current}" style="height:100%;width:100%"></div>`;
    const script = document.createElement("script");
    script.src = "https://s3.tradingview.com/tv.js";
    script.async = true;
    script.onload = () => {
      if (!window.TradingView) return;
      new window.TradingView.widget({
        container_id: uid.current,
        autosize: true,
        symbol: resolvedSymbol,
        interval,
        timezone: "Etc/UTC",
        theme: "dark",
        style: "1",
        locale: "en",
        toolbar_bg: "#0a0a0a",
        enable_publishing: false,
        allow_symbol_change: false,
        withdateranges: true,
        // Explicitly show every pro toolbar
        hide_side_toolbar: false,
        hide_top_toolbar: false,
        hide_legend: false,
        details: true,
        hotlist: false,
        calendar: false,
        studies: ["Volume@tv-basicstudies"],
        // Turn on advanced drawing + magnet + trade-panel features
        enabled_features: [
          "study_templates",
          "use_localstorage_for_settings",
          "side_toolbar_in_fullscreen_mode",
          "header_widget",
          "left_toolbar",
          "control_bar",
          "timeframes_toolbar",
          "edit_buttons_in_legend",
          "context_menus",
          "border_around_the_chart",
          "chart_property_page_trading",
          "trading_notifications",
        ],
        disabled_features: [
          "header_symbol_search",
          "header_compare",
          "symbol_search_hot_key",
          "popup_hints",
        ],
        save_image: true,
        overrides: {
          "paneProperties.background": "#0a0a0a",
          "paneProperties.vertGridProperties.color": "#1a1a1a",
          "paneProperties.horzGridProperties.color": "#1a1a1a",
          "mainSeriesProperties.candleStyle.upColor": "#10b981",
          "mainSeriesProperties.candleStyle.downColor": "#ef4444",
          "mainSeriesProperties.candleStyle.borderUpColor": "#10b981",
          "mainSeriesProperties.candleStyle.borderDownColor": "#ef4444",
          "mainSeriesProperties.candleStyle.wickUpColor": "#10b981",
          "mainSeriesProperties.candleStyle.wickDownColor": "#ef4444",
        },
      });
    };
    containerRef.current.appendChild(script);
    return () => { if (containerRef.current) containerRef.current.innerHTML = ""; };
  }, [resolvedSymbol, interval]);

  return (
    <div className="relative w-full bg-neutral-950" style={{ height }} data-testid="tv-chart" data-tv-symbol={resolvedSymbol}>
      <div ref={containerRef} className="w-full h-full" />
      <div className="pointer-events-none absolute bottom-3 left-3 flex items-center gap-1.5 text-white/40 text-[10px] font-mono tracking-widest uppercase select-none">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden><path d="M13 2 3 14h7l-1 8 10-12h-7l1-8z"/></svg>
        Tradelog · Prop Challenge
      </div>
    </div>
  );
}
